import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';

import type {
  ResolvedUser,
  SourceProduct,
  SourceContextType,
  MentionResult,
  MentionApiErrorData,
} from '../types';
import type { MentionInputContextValue } from './types';

const Context = createContext<MentionInputContextValue | null>(null);

export function useMentionInputContext(): MentionInputContextValue {
  const ctx = useContext(Context);
  if (!ctx)
    throw new Error('MentionInput sub-components must be used within <MentionInputComposable>');
  return ctx;
}

export function MentionInputProvider({
  entityId,
  sourceProduct,
  sourceRecordUrn,
  sourceContextType,
  sourceContextId,
  deepLinkUrl,
  notificationTargetDisplay,
  maxMentionsPerComment = 20,
  onMentionCreated,
  onMentionError,
  children,
}: {
  entityId: string;
  sourceProduct: SourceProduct;
  sourceRecordUrn: string;
  sourceContextType: SourceContextType;
  sourceContextId: string;
  deepLinkUrl: string;
  notificationTargetDisplay?: string | undefined;
  maxMentionsPerComment?: number | undefined;
  onMentionCreated?: ((mention: MentionResult) => void) | undefined;
  onMentionError?: ((error: MentionApiErrorData) => void) | undefined;
  children: ReactNode;
}) {
  const [mentionCount, setMentionCount] = useState(0);
  const [pendingWarning, setPendingWarning] = useState<{
    user: ResolvedUser;
    onConfirm: () => void;
  } | null>(null);
  const incrementMentionCount = useCallback(() => setMentionCount((c) => c + 1), []);

  return (
    <Context.Provider
      value={{
        entityId,
        sourceProduct,
        sourceRecordUrn,
        sourceContextType,
        sourceContextId,
        deepLinkUrl,
        notificationTargetDisplay,
        maxMentionsPerComment,
        mentionCount,
        incrementMentionCount,
        pendingWarning,
        setPendingWarning,
        onMentionCreated,
        onMentionError,
      }}
    >
      {children}
    </Context.Provider>
  );
}
